/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * homepage-webapp. Mirrors the listings-webapp pattern documented in
 * `docs/orion-feature-flags.md`:
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useLinearPaidMode`).
 *
 * homepage-webapp otherwise ramps via Statsig; Orion is introduced here so the
 * linear-ranker paid-weight sweep experiment shares a single `linear_paid_mode`
 * switch with the eb-ui discover surfaces and listings-webapp related events.
 * The same provider is also what makes `usePicoXTrack()` available to the PicoX
 * ad logger the interaction tracker runs (see `@/tracking`).
 *
 * To add a new flag: append one key + one default to `HomepageWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';
import { Stage } from '@eventbrite/webapp-constants';

// Paid-weight sweep arms for the linear ranker (search_service `linear_paid_mode`
// field). Each arm pins the `is_paid` slot at a fixed share of the final score
// (sweep_1..3 → 0/10/15%); control gets the base weight.
export const LINEAR_PAID_MODES = ['sweep_1', 'sweep_2', 'sweep_3'] as const;

export type LinearPaidMode = (typeof LINEAR_PAID_MODES)[number];

// Server-side validation for the client-supplied paid-sweep arm. The value arrives
// from an untrusted source (a JSON body field or a query param, which may be a
// non-string or a `string[]` for repeated params), so coerce it to a known arm or
// `undefined` — never forward it raw. Mirrors the client `.find` in `useLinearPaidMode`
// so the allowlist is authoritative on the server too, not merely advisory (the Orion
// gate stays the real authority for arm assignment).
export const coerceLinearPaidMode = (value: unknown): LinearPaidMode | undefined =>
    LINEAR_PAID_MODES.find((mode) => mode === value);

export type HomepageWebappSettings = {
    linear_paid_mode: string;
};

const DEFAULT_SETTINGS = createAppDefaults<HomepageWebappSettings>({
    linear_paid_mode: '',
});

// Both inputs here are deliberately chosen to resolve identically on the server render and
// the client hydration render. `Stage` is resolved server-side from `process.env.STAGE` and
// handed down as a prop (see `App.getInitialProps` in `_app.page.tsx`), and `NODE_ENV` is
// inlined at build time. Deriving this from `window.location.hostname` cannot be: `window`
// is undefined during SSR, so that render resolves every host to `production` and disagrees
// with the client — pointing Orion at production settings while serving a QA host.
//
// NODE_ENV is checked first because local dev runs with `STAGE=Test` (see `.env.development`)
// so that its service clients talk to QA; without this branch a local `next dev` would report
// `staging` rather than `development`.
const bspEnvironmentFromStage = (stage: Stage): 'staging' | 'development' | 'production' => {
    if (process.env.NODE_ENV === 'development') {
        return 'development';
    }
    if (stage === Stage.TEST) {
        return 'staging';
    }
    if (stage === Stage.DEV || stage === Stage.LOCAL) {
        return 'development';
    }
    return 'production';
};

export const useBspConfig = (stage: Stage) =>
    useMemo(
        () =>
            ({
                appName: 'homepage-webapp',
                environment: bspEnvironmentFromStage(stage),
                defaultOrionSettings: DEFAULT_SETTINGS,
                defaultSegmentations: {},
            }) as const,
        [stage],
    );

/**
 * Gate for the linear paid-weight sweep experiment (`linear_paid_mode` Orion
 * flag). This replaces the `enable_linear_score` gate once the linear ranker is
 * the search-service default: enrolled visitors get a sweep arm, and the
 * homepage browse search + popular-events carousel forward it as
 * `linear_paid_mode` (alongside `score_mode: 'linear'`) so the search service
 * applies that arm's paid weight under the linear ranker.
 *
 * Same `linear_paid_mode` key as the eb-ui discover surfaces (`useLinearPaidMode`)
 * and listings-webapp related events, so every surface ramps in lockstep off a
 * single switch. Returns `undefined` for control / unrecognised values.
 *
 * Returns `isLoading` so callers can hold their fetch until Orion settles —
 * otherwise a treatment visitor whose Orion resolves after the fetch fires
 * would be read as control and silently get the base paid weight.
 */
export const useLinearPaidMode = (): {
    paidMode: LinearPaidMode | undefined;
    isLoading: boolean;
} => {
    let settings: Partial<HomepageWebappSettings> = {};
    let isLoading = false;
    try {
        const state = useOrionSettings<HomepageWebappSettings>();
        settings = state.settings;
        isLoading = state.isLoading;
    } catch {
        // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    }
    const paidMode = LINEAR_PAID_MODES.find((mode) => mode === settings.linear_paid_mode);
    return { paidMode, isLoading };
};
